import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import ProgressBar from '@/components/ProgressBar';
import ReturnToDashboardPill from '@/components/ReturnToDashboardPill';
import PreparingScreen from '@/components/PreparingScreen';
import useDeepLinkParams from '@/hooks/useDeepLinkParams';
import useTaskCompletion from '@/hooks/useTaskCompletion';
import useAutoGenerate from '@/hooks/useAutoGenerate';
import useFeatureFlags from '@/hooks/useFeatureFlags';
import { botRunsAPI, projectsAPI } from '@/utils/api';
import { toast } from 'sonner';
import { Copy, CheckCircle, FloppyDisk } from '@phosphor-icons/react';
import TaskExecutionMeta from '@/components/TaskExecutionMeta';
import MissionFinalizeActions from '@/components/MissionFinalizeActions';

const ContentBotPage = () => {
  const navigate = useNavigate();
  const deepLink = useDeepLinkParams();
  const { completeIfDeepLinked } = useTaskCompletion();
  const flags = useFeatureFlags();
  const [step, setStep] = useState('input'); // 'input', 'running', 'result'
  const [status, setStatus] = useState('pending');
  const [runId, setRunId] = useState(null);
  const [formData, setFormData] = useState({
    niche: '',
    platform: 'Instagram',
    content_type: 'post',
    tone: 'professional',
    cta_type: 'engagement',
  });
  const [outputs, setOutputs] = useState(null);
  const [saving, setSaving] = useState(false);

  // Round 95 — Bot-page deep-link acceptance. When the customer
  // arrives via <TaskActionButton /> with an `asset_id`, log the
  // incoming context so ops can trace the workflow, and expose
  // it to the DOM for acceptance tests. Full edit-hydration
  // (fetch content by id + prefill form) is safe to add later
  // without changing this contract.
  useEffect(() => {
    if (deepLink.hasDeepLink) {
      console.info('[ContentBot] deep-link accepted', deepLink);
    }
  }, [deepLink.hasDeepLink, deepLink.asset_id, deepLink.task_id]);

  // Post-Round-96 execution loop — build inputs from existing
  // business memory so the customer never fills the form.
  //
  // Round 101 (2026-07-23) — when arriving from a Today's
  // Mission Generate chip, prefer the mission's OWN context
  // (deepLink.mission.*) over generic Business Memory so the
  // builder continues the exact mission the customer approved.
  //
  // Round 104 (2026-02-14) — "Approve & Generate". When the
  // dashboard threads `approved_edits` through `wctx`, those
  // fields are the customer's approved source of truth and
  // take priority over both `asset_payload_summary` and
  // Business Memory.
  const buildInputs = useCallback((memory) => {
    const m = deepLink.mission || {};
    const approved = m.approved_edits || {};
    const summary = m.asset_payload_summary || {};
    return {
      niche: m.niche || memory.niche || memory.business_name || formData.niche || '',
      platform:
        approved.platform
        || m.preferred_platform
        || summary.platform
        || formData.platform,
      content_type: approved.kind || summary.kind || formData.content_type,
      tone: memory.tone || formData.tone,
      cta_type: formData.cta_type,
      // Round 101 — pass the exact mission context through so
      // downstream generators can echo back the approved hook /
      // caption / CTA instead of inventing new copy.
      mission_context: (m && Object.keys(m).length) ? {
        asset_id: m.asset_id,
        asset_type: m.asset_type,
        mission_id: m.mission_id,
        mission_title: m.mission_title,
        // Round 104 — approved-edits wins; fall back to summary.
        approved_hook: approved.hook || summary.hook,
        approved_caption: approved.caption || summary.caption,
        approved_cta: approved.cta || summary.cta,
        approved_hashtags: approved.hashtags,
        approved_script: approved.script || approved.voice_script,
        approved_video_prompt: approved.video_prompt,
        publishing_instructions: approved.publishing_instructions
          || m.publishing_instructions,
        preferred_platform: approved.platform || m.preferred_platform,
      } : undefined,
      business_context: {
        business_name: m.business_name || memory.business_name || '',
        target_audience: m.target_audience || memory.target_audience || '',
        primary_offer: memory.primary_offer || '',
      },
    };
  }, [formData.platform, formData.content_type, formData.tone,
      formData.cta_type, formData.niche, deepLink.mission]);

  const handleAutoGenerateResult = useCallback((resp) => {
    setRunId(resp?.data?.id);
    setOutputs(resp?.data?.outputs);
    setStatus('completed');
    setStep('result');
    // Customer-oriented success (per Post-Round-96 UX principle #4).
    toast.success(
      "Great work! Today's content is ready.",
      { description: 'Review, refine, and approve when you\u2019re happy.' },
    );
  }, []);

  const auto = useAutoGenerate({
    botType: 'content',
    buildInputs,
    onResult: handleAutoGenerateResult,
    // Round 102 — the mission itself is the trigger. Auto-run
    // whenever a deep-link mission arrives, regardless of the
    // legacy `execution_loop_v1` flag (which stays for pages
    // without a mission-driven entry). Falls back to the flag
    // for the pre-mission surface (`/bots/content` visited
    // without a mission).
    enabled: Boolean(deepLink.mission) || Boolean(flags.execution_loop_v1),
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStep('running');
    setStatus('running');

    try {
      const response = await botRunsAPI.create({
        bot_type: 'content',
        inputs: formData,
      });

      setRunId(response.data.id);
      setOutputs(response.data.outputs);
      setStatus('completed');
      
      setTimeout(() => {
        setStep('result');
      }, 1000);
      
      toast.success('Content generated successfully!');
    } catch (error) {
      setStatus('failed');
      toast.error('Failed to generate content. Please try again.');
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const handleSave = async () => {
    const projectName = prompt('Enter a name for this project:');
    if (!projectName) return;

    setSaving(true);
    try {
      const res = await projectsAPI.create({
        project_name: projectName,
        bot_run_id: runId,
      });
      toast.success('Saved to Projects!');
      // Round 96 — close the Round-94 workflow loop when the
      // customer arrived via a deep-linked Dashboard task.
      await completeIfDeepLinked(
        { asset_id: res?.data?.id || runId, saved_at: new Date().toISOString() },
      );
    } catch (error) {
      toast.error('Failed to save project.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setStep('input');
    setStatus('pending');
    setOutputs(null);
    setRunId(null);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Round 95 — Return pill visible when arriving via deep link */}
        <div className="mb-4 flex items-center justify-between">
          <ReturnToDashboardPill testId="content-bot-return-pill" />
          {deepLink.hasDeepLink && (
            <span
              className="text-xs text-slate-500"
              data-testid="content-bot-deep-link-indicator"
              data-workflow-task-id={deepLink.task_id || ''}
              data-workflow-asset-id={deepLink.asset_id || ''}
              data-workflow-mode={deepLink.mode || ''}
              data-workflow-asset-type={deepLink.asset_type || ''}
            >
              Deep-linked from your Dashboard task
            </span>
          )}
        </div>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2" data-testid="content-bot-title">
            Content Bot
          </h1>
          <p className="text-slate-600">
            Generate hooks, post ideas, captions, and hashtags for your content
          </p>
        </div>

        {/* Round 102 — Mission continuation panel. Shown whenever
             the customer arrived via a Today's Mission Generate
             chip (`deepLink.mission` present) and the generic
             form has not been surfaced. Replaces the "What topic
             should I create content about?" landing. */}
        {deepLink.mission && (step === 'input' || auto.isPreparing) && !outputs && (
          <div
            className="mb-6 rounded-xl border border-purple-200 bg-white shadow-sm p-6"
            data-testid="content-bot-mission-continuation"
          >
            <div className="flex items-start justify-between gap-4 mb-3">
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-purple-600 mb-1">
                  Continuing your mission
                </p>
                <h2
                  className="text-xl font-semibold text-slate-900 truncate"
                  data-testid="content-bot-mission-title"
                >
                  {deepLink.mission.mission_title || 'Today\u2019s mission'}
                </h2>
                <p className="text-xs text-slate-500 mt-1">
                  {deepLink.mission.business_name || ''}
                  {deepLink.mission.niche ? ` \u00b7 ${deepLink.mission.niche}` : ''}
                  {deepLink.mission.preferred_platform
                    ? ` \u00b7 ${deepLink.mission.preferred_platform}` : ''}
                </p>
              </div>
              <span className="shrink-0 inline-flex items-center px-2 py-1 rounded-md bg-purple-100 text-purple-700 text-[10px] font-semibold uppercase tracking-wider">
                {deepLink.mission.asset_type || 'content'}
              </span>
            </div>
            {(deepLink.mission.asset_payload_summary
              && deepLink.mission.asset_payload_summary.hook) && (
              <div className="text-sm text-slate-600 leading-relaxed border-l-2 border-purple-200 pl-3 mt-3">
                {deepLink.mission.asset_payload_summary.hook}
              </div>
            )}
            {auto.isPreparing && (
              <p className="mt-4 text-xs text-slate-500 flex items-center gap-2">
                <span className="inline-block w-3 h-3 rounded-full border-2 border-purple-500 border-t-transparent animate-spin" />
                Generating from your approved mission&hellip;
              </p>
            )}
          </div>
        )}

        {/* Post-Round-96 execution loop — replace the blank form
             with a calm "Preparing your content" state while the
             auto-generator runs. Shown only when the customer
             arrived via a deep-link that carries auto_generate=1. */}
        {auto.isPreparing && !deepLink.mission && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
            <PreparingScreen
              assetType={auto.assetKind || 'generated_content'}
              testId="content-bot-preparing"
            />
          </div>
        )}

        {/* Input Form — hidden while auto-generation is in-flight
             AND whenever a Today's Mission is being continued
             (Round 102). The mission continuation panel above
             takes the entry-screen slot. */}
        {!auto.isPreparing && step === 'input' && !deepLink.mission && (
          <form onSubmit={handleSubmit} data-testid="content-bot-form" className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Your Niche *
              </label>
              <input
                type="text"
                name="niche"
                required
                data-testid="content-niche-input"
                value={formData.niche}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                placeholder="e.g., fitness, digital marketing, cooking"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Platform *
              </label>
              <select
                name="platform"
                data-testid="content-platform-select"
                value={formData.platform}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
              >
                <option value="Instagram">Instagram</option>
                <option value="TikTok">TikTok</option>
                <option value="Facebook">Facebook</option>
                <option value="YouTube">YouTube</option>
                <option value="Blog">Blog</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Content Type *
              </label>
              <select
                name="content_type"
                data-testid="content-type-select"
                value={formData.content_type}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
              >
                <option value="post">Post</option>
                <option value="reel script">Reel Script</option>
                <option value="video script">Video Script</option>
                <option value="caption">Caption</option>
                <option value="hook ideas">Hook Ideas</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Tone/Style *
              </label>
              <select
                name="tone"
                data-testid="content-tone-select"
                value={formData.tone}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
              >
                <option value="professional">Professional</option>
                <option value="casual">Casual</option>
                <option value="humorous">Humorous</option>
                <option value="inspirational">Inspirational</option>
                <option value="educational">Educational</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Call to Action Type *
              </label>
              <select
                name="cta_type"
                data-testid="content-cta-select"
                value={formData.cta_type}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
              >
                <option value="engagement">Engagement</option>
                <option value="link click">Link Click</option>
                <option value="share">Share</option>
                <option value="comment">Comment</option>
                <option value="save">Save</option>
              </select>
            </div>

            <button
              type="submit"
              data-testid="content-bot-submit"
              className="w-full py-4 px-6 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors"
            >
              Generate Content
            </button>
          </form>
        )}

        {/* Running State */}
        {step === 'running' && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 space-y-6" data-testid="content-bot-running">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              </div>
              <h3 className="text-xl font-semibold text-slate-900">Generating Your Content...</h3>
            </div>
            <ProgressBar status={status} />
          </div>
        )}

        {/* Results */}
        {step === 'result' && outputs && (
          <div className="space-y-6" data-testid="content-bot-results">
            {/* Round 104 — Approve & Generate finalize + return bar */}
            <MissionFinalizeActions
              outputs={outputs}
              botRunId={runId}
              projectName={(deepLink.mission && deepLink.mission.mission_title) || 'Content Mission'}
              buildMetaPatch={(o) => ({
                hook: Array.isArray(o?.hooks) && o.hooks.length ? o.hooks[0] : undefined,
                caption: Array.isArray(o?.captions) && o.captions.length ? o.captions[0] : undefined,
                hashtags: Array.isArray(o?.hashtags) && o.hashtags.length ? o.hashtags : undefined,
                // Round 106 — carry the strategist's best-practices
                // block into the mission's ``script`` field so the
                // dashboard ExecutionPanel shows the polished body.
                script: o?.best_practices || undefined,
              })}
              renderAsText={(o) => {
                const parts = [];
                if (Array.isArray(o?.hooks) && o.hooks.length) {
                  parts.push('HOOK\n' + o.hooks[0]);
                }
                if (Array.isArray(o?.captions) && o.captions.length) {
                  parts.push('CAPTION\n' + o.captions[0]);
                }
                if (Array.isArray(o?.hashtags) && o.hashtags.length) {
                  parts.push('HASHTAGS\n' + o.hashtags.join(' '));
                }
                if (o?.best_practices) {
                  parts.push('BEST PRACTICES\n' + o.best_practices);
                }
                return parts.join('\n\n');
              }}
              filenamePrefix="paygbot-content"
              testIdPrefix="content-bot-mission-actions"
            />

            {/* Actions */}
            <div className="flex flex-wrap gap-4">
              {/* Round 106 — hide the standalone "Save to Projects"
                  button when arriving via a Today's Mission deep
                  link. Mission-linked runs save via the
                  MissionFinalizeActions "Save & Finalize" button
                  above (which updates the ORIGINATING mission
                  asset). This prevents the strategist from
                  creating an orphan/duplicate asset. Direct bot
                  runs (no mission) still see this button. */}
              {!deepLink.hasDeepLink && (
                <button
                  onClick={handleSave}
                  disabled={saving}
                  data-testid="save-project-button"
                  className="flex items-center space-x-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
                >
                  <FloppyDisk size={20} weight="duotone" />
                  <span>{saving ? 'Saving...' : 'Save to Projects'}</span>
                </button>
              )}
              <button
                onClick={handleReset}
                data-testid="run-another-button"
                className="px-6 py-3 bg-white hover:bg-slate-50 text-slate-700 font-medium border border-slate-200 rounded-lg transition-colors"
              >
                Run Another
              </button>
              <button
                onClick={() => navigate('/my-creations')}
                data-testid="view-projects-button"
                className="px-6 py-3 bg-white hover:bg-slate-50 text-slate-700 font-medium border border-slate-200 rounded-lg transition-colors"
              >
                View Projects
              </button>
            </div>

            {/* Hooks */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-slate-900">Hooks</h3>
                <button 
                  onClick={() => handleCopy(outputs.hooks.join('\n\n'))} 
                  data-testid="copy-hooks-button"
                  className="flex items-center space-x-2 px-3 py-2 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
                  title="Copy all hooks"
                >
                  <Copy size={18} weight="duotone" />
                  <span>Copy</span>
                </button>
              </div>
              <div className="space-y-3">
                {outputs.hooks.map((hook, index) => (
                  <div key={`hook-${index}-${String(hook).slice(0, 20)}`} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <p className="text-slate-700">{hook}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Post Ideas */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-slate-900">Post Ideas</h3>
                <button 
                  onClick={() => handleCopy(outputs.post_ideas.map(i => typeof i === 'string' ? i : (i.title || '') + (i.description ? '\n' + i.description : '')).join('\n\n'))}
                  data-testid="copy-post-ideas-button"
                  className="flex items-center space-x-2 px-3 py-2 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
                  title="Copy all post ideas"
                >
                  <Copy size={18} weight="duotone" />
                  <span>Copy</span>
                </button>
              </div>
              <div className="space-y-3">
                {outputs.post_ideas.map((idea, index) => {
                  // Round 34 — backend wraps each idea in an object with
                  // title / format / description / cta / tier_asset /
                  // where_to_use. Keep string fallback for safety.
                  if (typeof idea === 'string') {
                    return (
                      <div key={index} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                        <p className="text-slate-700">{idea}</p>
                      </div>
                    );
                  }
                  return (
                    <div
                      key={`idea-${index}-${idea.title || ''}`}
                      data-testid={`content-post-idea-${index}`}
                      className="p-4 bg-slate-50 rounded-lg border border-slate-200"
                    >
                      <p className="font-medium text-slate-900">{idea.title}</p>
                      {idea.format && (
                        <p className="text-xs text-slate-500 mt-0.5">{idea.format}</p>
                      )}
                      {idea.description && (
                        <p className="text-sm text-slate-700 mt-2">{idea.description}</p>
                      )}
                      {idea.cta && (
                        <p className="text-xs text-slate-500 italic mt-1">CTA: {idea.cta}</p>
                      )}
                      {(idea.where_to_use || idea.tier_asset) && (
                        <TaskExecutionMeta
                          whereToUse={idea.where_to_use}
                          tierAsset={idea.tier_asset}
                          testIdPrefix={`content-idea-${index}`}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Captions */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-slate-900">Captions</h3>
                <button 
                  onClick={() => handleCopy(outputs.captions.join('\n\n'))} 
                  data-testid="copy-captions-button"
                  className="flex items-center space-x-2 px-3 py-2 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
                  title="Copy all captions"
                >
                  <Copy size={18} weight="duotone" />
                  <span>Copy</span>
                </button>
              </div>
              <div className="space-y-3">
                {outputs.captions.map((caption, index) => (
                  <div key={`caption-${index}-${String(caption).slice(0, 20)}`} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <p className="text-slate-700">{caption}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Hashtags */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-slate-900">Hashtags</h3>
                <button 
                  onClick={() => handleCopy(outputs.hashtags.join(' '))} 
                  data-testid="copy-hashtags-button"
                  className="flex items-center space-x-2 px-3 py-2 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
                  title="Copy all hashtags"
                >
                  <Copy size={18} weight="duotone" />
                  <span>Copy</span>
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {outputs.hashtags.map((tag, index) => (
                  <span key={`${index}-${tag}`} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Best Practices */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-8 space-y-4">
              <div className="flex items-start space-x-3">
                <CheckCircle size={24} weight="fill" className="text-emerald-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-emerald-900 mb-2">Best Practices</h3>
                  <pre className="text-sm text-emerald-800 whitespace-pre-wrap font-sans">{outputs.best_practices}</pre>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContentBotPage;
